import { useEffect, useRef, useState } from "react";
import API from "../services/API";
import { extractErrorMessage } from "../utils/errorMessage";

/* ==========================================================================
 * In-app bug report modal.
 *
 * Why a window event instead of context/props?
 * The trigger lives in the Sidebar (rendered alongside SidebarLayout,
 * not below it), and we want any descendant of any role to be able
 * to fire`openBugReportModal()`without threading callbacks through
 * six layers. A`CustomEvent`is the cleanest decoupling primitive
 * the platform offers and it Just Works™ across StrictMode mounts.
 *
 * Accent prop:
 * Different roles get different highlight tones (admin = rose, manager
 * = indigo, employee = emerald). This keeps the modal visually grounded
 * in whichever surface launched it without forcing a CSS-vars
 * refactor.
 * ======================================================================== */

import { BUGREPORT_OPEN_EVENT } from "../services/bugreport";

type Severity = "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
const SEVERITIES: Severity[] = ["LOW","MEDIUM","HIGH","CRITICAL"];

type Accent = "indigo" | "rose" | "emerald";

interface BugReportModalProps {
 accent?: Accent;
}

const ACCENT: Record<
 Accent,
 {
 ring: string;
 button: string;
 icon: string;
 focus: string;
 }
> = {
 indigo: {
 ring: "ring-emerald-200",
 button: "bg-role-primary hover:brightness-110 focus:ring-role-primary",
 icon: "text-role-primary",
 focus: "focus:border-role-primary focus:ring-emerald-200",
 },
 rose: {
 ring: "ring-rose-500/20",
 button: "bg-rose-600 hover:bg-rose-700 focus:ring-rose-500",
 icon: "text-rose-600",
 focus: "focus:border-rose-500 focus:ring-rose-500/20",
 },
 emerald: {
 ring: "ring-emerald-200",
 button: "bg-role-primary hover:brightness-110 focus:ring-role-primary",
 icon: "text-role-primary",
 focus: "focus:border-role-primary focus:ring-emerald-200",
 },
};

interface BugCreatedSummary {
 bug_id: number;
 attached_logs: number;
}

const initialFormState = {
 title: "",
 description: "",
 severity: "MEDIUM"as Severity,
 pageUrl: "",
 browserInfo: "",
};

export default function BugReportModal({ accent ="indigo"}: BugReportModalProps) {
 const tones = ACCENT[accent];
 const [open, setOpen] = useState(false);
 const [form, setForm] = useState(initialFormState);
 const [submitting, setSubmitting] = useState(false);
 const [error, setError] = useState<string | null>(null);
 const [success, setSuccess] = useState<BugCreatedSummary | null>(null);

 const dialogRef = useRef<HTMLDivElement | null>(null);
 const titleRef = useRef<HTMLInputElement | null>(null);

 useEffect(() => {
 const handleOpen = () => {
 // Snapshot environment data automatically — pre-fills the form
 // so reporters don't have to remember which page broke.
 const url = typeof window !=="undefined"? window.location.href :"";
 const ua = typeof window !=="undefined"? window.navigator.userAgent :"";
 setForm((prev) => ({
 ...initialFormState,
 pageUrl: prev.pageUrl || url.slice(0, 500),
 browserInfo: prev.browserInfo || ua.slice(0, 500),
 }));
 setError(null);
 setSuccess(null);
 setOpen(true);
 };
 window.addEventListener(BUGREPORT_OPEN_EVENT, handleOpen);
 return () => window.removeEventListener(BUGREPORT_OPEN_EVENT, handleOpen);
 }, []);

 useEffect(() => {
 if (!open) return;
 const onKey = (e: KeyboardEvent) => {
 if (e.key ==="Escape") {
 e.preventDefault();
 setOpen(false);
 }
 };
 window.addEventListener("keydown", onKey);
 // Trap focus loosely — return focus to the title field on open.
 window.setTimeout(() => titleRef.current?.focus(), 50);
 return () => window.removeEventListener("keydown", onKey);
 }, [open]);

 const close = () => {
 setOpen(false);
 setForm(initialFormState);
 };

 const onChange = (
 e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
 ) => {
 const { name, value } = e.target;
 setForm((prev) => ({ ...prev, [name]: value }));
 };

 const submit = async (e: React.FormEvent) => {
 e.preventDefault();
 setError(null);

 const title = form.title.trim();
 const desc = form.description.trim();
 if (!title || title.length > 200) {
 setError("Title is required (1–200 chars).");
 return;
 }
 if (desc.length < 5) {
 setError("Please describe what went wrong in at least 5 characters.");
 return;
 }

 setSubmitting(true);
 try {
 const res = await API.post<{ data: BugCreatedSummary }>("/api/bugs", {
 title,
 description: desc,
 severity: form.severity,
 pageUrl: form.pageUrl.trim().slice(0, 500) || null,
 browserInfo: form.browserInfo.trim().slice(0, 500) || null,
 });
 const data = res.data?.data ?? { bug_id: 0, attached_logs: 0 };
 setSuccess(data);
 setForm(initialFormState);
 } catch (err: unknown) {
 setError(extractErrorMessage(err,"Could not file the bug. Try again."));
 } finally {
 setSubmitting(false);
 }
 };

 if (!open) return null;

 return (
 <div
 className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm"role="dialog"aria-modal="true"aria-label="Report a bug"onClick={(e) => {
 if (e.target === e.currentTarget) close();
 }}
 >
 <div
 ref={dialogRef}
 className={`w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ${tones.ring}`}
 >
 <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-4">
 <div className="flex items-center gap-3">
 <span
 className={`flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 ${tones.icon}`}
 aria-hidden
 >
 <svg viewBox="0 0 24 24"className="h-5 w-5"fill="none"stroke="currentColor"strokeWidth="2"strokeLinecap="round"strokeLinejoin="round">
 <rect x="8"y="6"width="8"height="14"rx="4"/>
 <path d="M12 2v4M9 9l-5-3M15 9l5-3M9 15l-5 3M15 15l5 3"/>
 </svg>
 </span>
 <div>
 <h2 className="text-base font-semibold text-slate-900">
 Report a bug
 </h2>
 <p className="mt-0.5 text-xs text-slate-500">
 We attach your recent activity to help us reproduce.
 </p>
 </div>
 </div>
 <button
 type="button"onClick={close}
 className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"aria-label="Close">
 <svg viewBox="0 0 24 24"className="h-5 w-5"fill="none"stroke="currentColor"strokeWidth="2"strokeLinecap="round"strokeLinejoin="round">
 <line x1="18"y1="6"x2="6"y2="18"/>
 <line x1="6"y1="6"x2="18"y2="18"/>
 </svg>
 </button>
 </header>

 {success ? (
 <div className="px-6 py-6 text-sm text-slate-700">
 <p className="font-semibold text-slate-900">
 Thanks — your bug is logged.
 </p>
 <p className="mt-2">
 <span className="font-mono text-xs">
 #{success.bug_id}
 </span>{""}
 — we attached <strong>{success.attached_logs}</strong>{""}
 recent audit log{success.attached_logs === 1 ?"":"s"} from your session for the team.
 </p>
 <div className="mt-6 flex justify-end">
 <button
 type="button"onClick={close}
 className={`inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${tones.button}`}
 >
 Close
 </button>
 </div>
 </div>
 ) : (
 <form onSubmit={submit} className="space-y-4 px-6 py-5">
 {error && (
 <div
 role="alert"className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
 {error}
 </div>
 )}

 <div>
 <label htmlFor="bug-title"className="text-sm font-medium text-slate-700">
 Title <span className="text-rose-500">*</span>
 </label>
 <input
 id="bug-title"name="title"ref={titleRef}
 value={form.title}
 onChange={onChange}
 maxLength={200}
 className={`mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 ${tones.focus}`}
 placeholder="What went wrong, in one short line?"/>
 </div>

 <div>
 <label htmlFor="bug-description"className="text-sm font-medium text-slate-700">
 Description <span className="text-rose-500">*</span>
 </label>
 <textarea
 id="bug-description"name="description"value={form.description}
 onChange={onChange}
 rows={4}
 className={`mt-1 w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 ${tones.focus}`}
 placeholder="Steps to reproduce, expected vs. actual, etc. (min 5 chars)"/>
 </div>

 <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
 <div>
 <label htmlFor="bug-severity"className="text-sm font-medium text-slate-700">
 Severity
 </label>
 <select
 id="bug-severity"name="severity"value={form.severity}
 onChange={onChange}
 className={`mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 ${tones.focus}`}
 >
 {SEVERITIES.map((s) => (
 <option key={s} value={s}>
 {s}
 </option>
 ))}
 </select>
 </div>
 <div>
 <label htmlFor="bug-page"className="text-sm font-medium text-slate-700">
 Page URL
 </label>
 <input
 id="bug-page"name="pageUrl"value={form.pageUrl}
 onChange={onChange}
 maxLength={500}
 className={`mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 ${tones.focus}`}
 />
 </div>
 </div>

 <div>
 <label htmlFor="bug-browser"className="text-sm font-medium text-slate-700">
 Browser info
 </label>
 <input
 id="bug-browser"name="browserInfo"value={form.browserInfo}
 onChange={onChange}
 maxLength={500}
 className={`mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 ${tones.focus}`}
 />
 <p className="mt-1 text-xs text-slate-500">
 Auto-filled from your browser; edit if it's wrong.
 </p>
 </div>

 <div className="flex items-center justify-end gap-2 border-t border-slate-200 pt-4">
 <button
 type="button"onClick={close}
 disabled={submitting}
 className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60">
 Cancel
 </button>
 <button
 type="submit"disabled={submitting}
 className={`inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60 ${tones.button}`}
 >
 {submitting ?"Filing…":"File bug"}
 </button>
 </div>
 </form>
 )}
 </div>
 </div>
 );
}
